import styled, { css } from "styled-components/macro";
import { mediaQueries } from "../../styles/media";
import PageHeader from "../../components/PageHeader";
import Prose from "../../components/Prose";

export const StyledEnquiryDoablePageHeader = styled(PageHeader)`
  margin-bottom: 16px;
`;

export const StyledJunoEnquiry = styled.div`
  position: relative;
  color: var(--juno-color-text);
  background-color: var(--juno-color-white);
  border: 1px solid var(--juno-color-indigo);
  border-radius: 16px;
  padding: 0;

  margin-left: 20px;
  margin-right: 20px;
  margin-bottom: 16px;

  ${mediaQueries.md} {
    margin-left: 0px;
    margin-right: 64px;
  }
`;

// The card is padding: 0 so its heading bar sits flush; each content region below insets
// itself with this shared padding. spacingScale[4] = 16px, matching DashboardCard.
const enquiryBlockPadding = css`
  padding: var(--juno-space-4);
`;

// Markdown headings in an enquiry message should read as bold body text, not as a larger
// heading inside the bubble, so flatten every heading to the body size.
export const StyledEnquiryMessage = styled(Prose).attrs({ $variant: "compact" })`
  --prose-h1-size: 1em;
  --prose-h2-size: 1em;
  --prose-h3-size: 1em;

  ${enquiryBlockPadding}
`;

// A client's reply: the same block as Juno's message, plus pre-line to keep the single
// line breaks within a paragraph (see ClientReply for the paragraph split).
export const StyledClientReply = styled(StyledEnquiryMessage)`
  white-space: pre-line;
`;

// A legacy plain-text Juno message, shown until webapp-alpha #30268 sends HTML for every
// message. pre-line preserves the newlines the message was written with. Temporary: see
// JUNO-3018, remove with the plain branch in EnquiryDoable once #30268 has deployed.
export const StyledPlainEnquiryMessage = styled(StyledEnquiryMessage)`
  white-space: pre-line;
`;

export const StyledReplyForm = styled.div`
  ${enquiryBlockPadding}
  font-size: 14px;
  ${mediaQueries.md} {
    font-size: 16px;
  }

  label,
  label > div {
    font-size: 14px;
    ${mediaQueries.md} {
      font-size: 16px;
    }
  }

  textarea {
    border-width: 1px;
    border-radius: 16px;
    font-size: 14px;
    ${mediaQueries.md} {
      font-size: 16px;
    }
    padding: 12px;
  }

  button {
    font-size: var(--juno-font-size-body-sm);
  }
`;

// Insets the standalone "send a reply" link within the card.
export const StyledReplyPrompt = styled.div`
  ${enquiryBlockPadding}
`;

export const StyledJunoEnquiryActivity = styled.div`
  position: relative;
  color: var(--juno-color-text);
  background-color: var(--juno-color-white);
  border: 1px solid var(--juno-color-indigo);
  border-radius: 16px;
  padding: 0;

  margin-left: 20px;
  margin-right: 20px;
  margin-bottom: 16px;

  ${mediaQueries.md} {
    margin-left: 0px;
    margin-right: 64px;
  }

  &:after {
    content: "";
    position: absolute;
    left: 0;
    top: 16px;
    width: 0;
    height: 0;
    border: 8px solid transparent;
    border-right-color: var(--juno-color-indigo);
    border-left: 0;
    border-top: 0;
    margin-top: -4px;
    margin-left: -9px;
  }
`;

export const StyledEnquiryActivity = styled.div`
  position: relative;
  color: var(--juno-color-text);
  background-color: var(--juno-color-white);
  border: 1px solid var(--juno-color-light-grey);
  border-radius: 16px;
  padding: 0;

  margin-left: 20px;
  margin-right: 20px;
  margin-bottom: 16px;

  ${mediaQueries.md} {
    margin-left: 64px;
    margin-right: 0;
  }

  &:after {
    content: "";
    position: absolute;
    right: 0;
    top: 24px;
    width: 0;
    height: 0;
    border: 8px solid transparent;
    border-left-color: var(--juno-color-light-grey);
    border-right: 0;
    border-bottom: 0;
    margin-top: -4px;
    margin-right: -9px;
  }
`;

export const StyledJunoEnquiryHeading = styled.div`
  display: flex;
  justify-content: space-between;

  font-weight: var(--juno-font-weight-bold);
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  color: var(--juno-color-white);
  padding: 6px 16px;
  background: var(--juno-color-indigo);
  border-bottom: 1px solid var(--juno-color-indigo);
  font-size: 14px;
`;

export const StyledEnquiryHeading = styled.div`
  display: flex;
  justify-content: space-between;

  font-weight: var(--juno-font-weight-bold);
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  color: var(--juno-color-white);
  padding: 6px 16px;
  background: var(--juno-color-light-grey);
  border-bottom: 1px solid var(--juno-color-light-grey);
  font-size: 14px;
`;

export const StyledAttachmentList = styled.ul`
  margin: 0;
  padding: 0 16px 8px 16px;

  li {
    display: flex;
    align-items: center;
    font-size: 14px;
    margin-bottom: 4px;
    line-height: normal;
    font-weight: var(--juno-font-weight-bold);
    color: var(--juno-color-grey);
  }

  a {
    color: var(--juno-color-grey);
    border-bottom: 1px solid transparent;

    &:hover {
      color: var(--juno-color-link-hover);
      border-bottom-color: var(--juno-color-link-hover);
    }
  }

  svg {
    transform: rotate(45deg);
    margin-left: -4px;
    font-size: 18px;
    margin-right: 4px;
    color: var(--juno-color-light-grey);
  }
`;
